import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  message,
  Space,
  Typography,
  Divider,
  Row,
  Col,
  ColorPicker,
  Switch,
  Alert,
  Spin
} from 'antd';
import {
  UploadOutlined,
  SaveOutlined,
  ReloadOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const Branding = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [brandingData, setBrandingData] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);

  useEffect(() => {
    fetchBrandingSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBrandingSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/agency/branding');
      setBrandingData(response.data);
      form.setFieldsValue({
        companyName: response.data.companyName || '',
        domain: response.data.domain || '',
        primaryColor: response.data.primaryColor || '#1890ff',
        secondaryColor: response.data.secondaryColor || '#52c41a',
        accentColor: response.data.accentColor || '#faad14',
        customCSS: response.data.customCSS || '',
        emailFromName: response.data.emailFromName || '',
        emailFromAddress: response.data.emailFromAddress || '',
        supportEmail: response.data.supportEmail || '',
        enableWhiteLabel: response.data.enableWhiteLabel || false
      });
    } catch (error) {
      console.error('Error fetching branding settings:', error);
      message.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const formData = new FormData();
      
      // Add form values
      Object.keys(values).forEach(key => {
        if (values[key] !== undefined && values[key] !== null) {
          if (typeof values[key] === 'object' && values[key].toHexString) {
            // Handle ColorPicker value
            formData.append(key, values[key].toHexString());
          } else {
            formData.append(key, values[key]);
          }
        }
      });

      // Add files if selected
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      if (faviconFile) {
        formData.append('favicon', faviconFile);
      }

      await axios.post('/api/agency/branding', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      message.success('Branding settings saved successfully');
      fetchBrandingSettings();
      setLogoFile(null);
      setFaviconFile(null);
    } catch (error) {
      console.error('Error saving branding settings:', error);
      message.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setLogoFile(null);
    setFaviconFile(null);
    fetchBrandingSettings();
  };

  const logoUploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('Image must be smaller than 2MB!');
        return false;
      }
      setLogoFile(file);
      return false;
    },
    onRemove: () => {
      setLogoFile(null);
    },
    fileList: logoFile ? [logoFile] : []
  };

  const faviconUploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return false;
      }
      const isLt1M = file.size / 1024 / 1024 < 1;
      if (!isLt1M) {
        message.error('Favicon must be smaller than 1MB!');
        return false;
      }
      setFaviconFile(file);
      return false;
    },
    onRemove: () => {
      setFaviconFile(null);
    },
    fileList: faviconFile ? [faviconFile] : []
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>White Label Branding</Title>
      <Paragraph type="secondary">
        Configure your corporate branding settings. These settings will apply to all locations under your enterprise account.
      </Paragraph>

      <Alert
        message="Corporate Branding Only"
        description="These branding settings are managed at the corporate level and apply to all locations. Individual locations cannot modify these settings."
        type="info"
        icon={<InfoCircleOutlined />}
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          enableWhiteLabel: false,
          primaryColor: '#1890ff',
          secondaryColor: '#52c41a',
          accentColor: '#faad14'
        }}
      >
        <Card title="General Settings" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Company Name"
                name="companyName"
                rules={[{ required: true, message: 'Please enter company name' }]}
              >
                <Input placeholder="Enter your company name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Custom Domain"
                name="domain"
                rules={[
                  { required: true, message: 'Please enter domain' },
                  { type: 'url', message: 'Please enter a valid URL' }
                ]}
              >
                <Input placeholder="https://yourdomain.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Enable White Label"
            name="enableWhiteLabel"
            valuePropName="checked"
            extra="Enable to use custom branding across the platform"
          >
            <Switch />
          </Form.Item>
        </Card>

        <Card title="Logo & Favicon" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Company Logo"
                extra="Recommended size: 200x60px, Max size: 2MB"
              >
                <Upload {...logoUploadProps} maxCount={1}>
                  <Button icon={<UploadOutlined />}>Select Logo</Button>
                </Upload>
                {brandingData?.logoUrl && !logoFile && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Current logo:</Text>
                    <br />
                    <img
                      src={brandingData.logoUrl}
                      alt="Current logo"
                      style={{ maxWidth: 200, marginTop: 8, border: '1px solid #d9d9d9', padding: 8 }}
                    />
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Favicon"
                extra="Recommended size: 32x32px or 64x64px, Max size: 1MB"
              >
                <Upload {...faviconUploadProps} maxCount={1}>
                  <Button icon={<UploadOutlined />}>Select Favicon</Button>
                </Upload>
                {brandingData?.faviconUrl && !faviconFile && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Current favicon:</Text>
                    <br />
                    <img
                      src={brandingData.faviconUrl}
                      alt="Current favicon"
                      style={{ maxWidth: 32, marginTop: 8, border: '1px solid #d9d9d9', padding: 4 }}
                    />
                  </div>
                )}
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Color Scheme" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Primary Color"
                name="primaryColor"
                rules={[{ required: true, message: 'Please select primary color' }]}
              >
                <ColorPicker showText format="hex" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Secondary Color"
                name="secondaryColor"
                rules={[{ required: true, message: 'Please select secondary color' }]}
              >
                <ColorPicker showText format="hex" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Accent Color"
                name="accentColor"
                rules={[{ required: true, message: 'Please select accent color' }]}
              >
                <ColorPicker showText format="hex" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Custom CSS"
            name="customCSS"
            extra="Advanced: Add custom CSS to further customize the appearance"
          >
            <Input.TextArea
              rows={6}
              placeholder="/* Enter custom CSS here */"
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Card>

        <Card title="Email Branding" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Email From Name"
                name="emailFromName"
                rules={[{ required: true, message: 'Please enter from name' }]}
              >
                <Input placeholder="Your Company Name" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Email From Address"
                name="emailFromAddress"
                rules={[
                  { required: true, message: 'Please enter email address' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input placeholder="noreply@yourdomain.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Support Email"
                name="supportEmail"
                rules={[
                  { required: true, message: 'Please enter support email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input placeholder="support@yourdomain.com" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Divider />

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saving}
              size="large"
            >
              Save Branding Settings
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              size="large"
            >
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default Branding;
